import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import {
  Button,
  Grid,
  Typography,
} from '@mui/material';
import { useNavigate, Link } from "react-router-dom";
import { useGoogleLogin } from '@react-oauth/google';
import { useForm } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import AppleLogin from 'react-apple-login';
import isEmpty from 'lodash/isEmpty';

import { Context, useAuth } from '../../context/auth';
import { CustomInput } from '../CustomInput';
import { isMobileAtom } from '../../atoms';
import styles from './style';

interface SignInData {
  email: string;
  password: string;
}

interface SignUpArgs {
  email?: string;
  idToken?: string;
  password?: string;
  username?: string;
  dateOfBirth?: Date;
  provider?: string;
  userAppleId?: string;
}

interface LoginFormProps {
  // In the login popup: the popup shows its own title.
  embedded?: boolean;
  // Replaces the navigation to /signup (ex: the popup switches to its sign up form).
  onSwitchToSignup?: () => void;
  // Replaces the navigation to /forgotpassword (ex: the popup switches to its forgot password form).
  onForgotPassword?: () => void;
  // A social account without SoSkills account: replaces the navigation to /signup with its data.
  onSocialSignUp?: (prefill: SignUpArgs) => void;
}

// Login form: credentials, social logins and the link to the sign up. Fills its container.
const LoginForm: React.FC<LoginFormProps> = React.memo((props) => {
  const {
    embedded = false,
    onSwitchToSignup,
    onForgotPassword,
    onSocialSignUp,
  } = props;
  const {
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
  } = useAuth() as Context;
  const {
    control,
    handleSubmit,
  } = useForm<SignInData>();
  const isMobile = useAtomValue(isMobileAtom);
  const navigate = useNavigate();
  // The same message for a wrong email and a wrong password: it doesn't tell which accounts exist.
  // Shown under the password field, both fields in red.
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();
  const { hostname, protocol, port } = window.location;
  const redirect_uri = protocol + "//" + hostname + (port ? ":" + port : "");
  const forgotPasswordLinkTrad = t('form.forgotPasswordLink');
  const connexionTrad = t('form.signIn');
  const errorSignInContentTrad = t('form.error.invalidCredentials');
  const emailTrad = t('email');
  const passwordTrad = t('form.password');
  const formPasswordTrad = t('form.password');
  const formErrorRequiredPasswordTrad = t('form.error.required.password');
  const formErrorMinLengthPasswordTrad = t('form.error.minLength.password');
  const formErrorRequiredEmailTrad = t('form.error.required.email');
  const formErrorPatternEmail = t('form.error.pattern.email');
  const googleSignInTrad = t('googleSignIn');
  const formSignUpTrad = t('form.signUp');
  const formSignUpQuestionTrad = t('form.signUpQuestion');
  const appleSignInTrad = t('appleSignIn');
  const facebookSignInTrad = t('facebookSignIn');

  // The popup has its own title right above.
  const firstFieldMt = embedded ? 0 : 2;

  const goToSignUp = useCallback((res: SignUpArgs) => {
    if (onSocialSignUp) {
      onSocialSignUp(res);
      return;
    }
    navigate('/signup', { state: res, replace: true });
  }, [navigate, onSocialSignUp]);

  const loginGoogle = useGoogleLogin(
    {
      onSuccess: async (tokenResponse: any) => {
        const res = await signInWithGoogle(tokenResponse) as unknown as SignUpArgs;
        if (res && !isEmpty(res) && res.provider === 'Google') {
          goToSignUp(res);
        }
      },
      flow: 'auth-code',
    }
  );

  const onClickGoogle = useCallback(() => {
    loginGoogle();
  }, [loginGoogle]);

  const onSubmit = useCallback(async (obj: SignInData) => {
    setErrorMessage('');
    try {
      await signIn(obj.email, obj.password);
    } catch (e: any) {
      setErrorMessage(errorSignInContentTrad);
    }
  }, [errorSignInContentTrad, signIn]);

  // Typing again: the error of the last try goes away.
  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  const loginApple = useCallback(async (response: any) => {
    const res = await signInWithApple(response) as unknown as SignUpArgs;
    if (res && !isEmpty(res) && res.provider === 'Apple') {
      goToSignUp(res);
    }
  }, [goToSignUp, signInWithApple]);

  const loginOnEnter = useCallback(async () => {
    handleSubmit((data) => {
      if (data.email && data.password) {
        onSubmit(data);
      }
    })() as any;
  }, [handleSubmit, onSubmit]);

  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === 'Enter') {
        event.preventDefault();
        loginOnEnter();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [handleSubmit, onSubmit, loginOnEnter]);

  const emailRules = useMemo(() => ({
    required: formErrorRequiredEmailTrad,
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: formErrorPatternEmail,
    },
  }), [formErrorPatternEmail, formErrorRequiredEmailTrad])

  const passwordRules = useMemo(() => ({
    required: formErrorRequiredPasswordTrad,
    minLength: {
      value: 8,
      message: formErrorMinLengthPasswordTrad,
    },
  }), [formErrorRequiredPasswordTrad, formErrorMinLengthPasswordTrad]);

  const facebookCallback = useCallback(async (response: any) => {
    const res = await signInWithFacebook(response) as unknown as SignUpArgs;
    if (res && !isEmpty(res) && res.provider === 'Facebook') {
      goToSignUp(res);
    }
  }, [goToSignUp, signInWithFacebook]);

  const onClickForgotPassword = useCallback((e: React.MouseEvent) => {
    if (onForgotPassword) {
      e.preventDefault();
      onForgotPassword();
    }
  }, [onForgotPassword]);

  const onClickSignup = useCallback((e: React.MouseEvent) => {
    if (onSwitchToSignup) {
      e.preventDefault();
      onSwitchToSignup();
    }
  }, [onSwitchToSignup]);

  const facebookRender = useCallback((renderProps: any) => (
    <Button
      startIcon={
        <img
          alt=''
          src={`${process.env.REACT_APP_AWS_CLOUDFRONT_URL}providers/facebook.webp`}
          width={20}
          height={20}
        />
      }
      variant="contained"
      onClick={renderProps.onClick}
      color="primary"
      fullWidth
      sx={styles.facebookButton}
    >
      <Typography fontWeight={500} fontFamily="Poppins">
        {facebookSignInTrad}
      </Typography>
    </Button>
  ), [facebookSignInTrad]);

  const appleRender = useCallback((renderProps: any) => (
    <Button
      startIcon={
        <img
          alt=''
          src={`${process.env.REACT_APP_AWS_CLOUDFRONT_URL}providers/apple.webp`}
          width={20}
          height={20}
        />
      }
      variant="contained"
      onClick={renderProps.onClick}
      color="primary"
      fullWidth
      sx={styles.appleButton}
    >
      <Typography fontWeight={500} fontFamily="Poppins">
        {appleSignInTrad}
      </Typography>
    </Button>
  ), [appleSignInTrad]);

  const signupTypo = useMemo(() => ({
    ...styles.smallLink,
    marginLeft: isMobile ? '1vw' : '0.5vw',
  }), [isMobile]);

  const googleStartIcon = useMemo(() => (<img
    alt=''
    src={`${process.env.REACT_APP_AWS_CLOUDFRONT_URL}providers/google.webp`}
    width={20}
    height={20}
  />), []);

  return (
    <>
      <Grid container>
        <Grid item mt={firstFieldMt} xs={12}>
          <CustomInput
            dark
            name="email"
            autoComplete="email"
            placeholder={emailTrad}
            control={control}
            label={'Email'}
            rules={emailRules}
            fullWidth
            variant="outlined"
            forcedError={!!errorMessage}
            onChange={clearError}
          />
        </Grid>
        <Grid item xs={12} mt={2}>
          <CustomInput
            dark
            name="password"
            autoComplete="current-password"
            label={passwordTrad}
            placeholder={formPasswordTrad}
            control={control}
            rules={passwordRules}
            fullWidth
            variant="outlined"
            type="password"
            forcedError={errorMessage}
            onChange={clearError}
          />
        </Grid>
        <Grid
          item
          mt={1}
          sm={12}
        >
          <Typography sx={styles.forgotText}>
            <Link
              to="/forgotpassword"
              style={styles.linkPassword}
              onClick={onClickForgotPassword}
            >
              {forgotPasswordLinkTrad}
            </Link>
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          mt={1.5}
        >
          <Button
            onClick={handleSubmit(onSubmit)}
            fullWidth
            sx={styles.buttonConnexion}
          >
            <Typography fontWeight={500} fontFamily="Poppins">
              {connexionTrad}
            </Typography>
          </Button>
        </Grid>
        <Grid
          item
          mt={1}
          xs={12}
        >
          <Button
            startIcon={googleStartIcon}
            variant="contained"
            onClick={onClickGoogle}
            color="primary"
            fullWidth
            sx={styles.googleButton}
          >
            <Typography fontWeight={500} fontFamily="Poppins">
              {googleSignInTrad}
            </Typography>
          </Button>
        </Grid>
        <Grid
          item
          mt={1}
          xs={12}
        >
          <FacebookLogin
            autoLoad={false}
            isMobile={isMobile}
            disableMobileRedirect={true}
            redirectUri={redirect_uri}
            appId={process.env.REACT_APP_FACEBOOK_APP_ID!}
            callback={facebookCallback}
            render={facebookRender}
          />
        </Grid>
        <Grid
          item
          mt={1}
          xs={12}
        >
          <AppleLogin
            clientId="io.soskills.webapp"
            redirectURI={redirect_uri}
            responseType={'code id_token'}
            responseMode={'form_post'}
            usePopup={true}
            callback={loginApple} // Catch the response
            scope="email name"
            //responseMode="query"
            render={appleRender}
          />
        </Grid>
        {/* On a phone only admins log in: players sign up in the app. */}
        {!isMobile && (
          <Grid
            item
            mt={1.5}
            mb={0}
            xs={12}
            flexDirection="row"
            display="flex"
            justifyContent="flex-end"
          >
            <Typography display="inline" sx={styles.smallText}>
              {formSignUpQuestionTrad}
            </Typography>
            <Typography display="inline" sx={signupTypo}>
              <Link to="/signup" style={styles.linkTypo} onClick={onClickSignup}>
                {formSignUpTrad}
              </Link>
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  );
});

export default LoginForm;
